// Store logo service with multiple fallback options
export interface StoreLogoData {
  name: string;
  primaryLogo: string;
  fallbackLogos: string[];
  emoji: string;
  color: string;
  brandColors: {
    primary: string;
    secondary?: string;
  };
}

export const STORE_LOGOS: Record<string, StoreLogoData> = {
  'K-Market': {
    name: 'K-Market',
    primaryLogo: 'https://logo.clearbit.com/kesko.fi',
    fallbackLogos: [
      'https://www.google.com/s2/favicons?domain=k-ruoka.fi&sz=64',
      'https://cdn.worldvectorlogo.com/logos/kesko-1.svg',
    ],
    emoji: '🛒',
    color: 'bg-orange-500',
    brandColors: {
      primary: '#FF6B35', // K-Group orange
      secondary: '#FFF'
    }
  },
  'S-Market': {
    name: 'S-Market',
    primaryLogo: 'https://logo.clearbit.com/s-kanava.fi',
    fallbackLogos: [
      'https://www.google.com/s2/favicons?domain=s-kanava.fi&sz=64',
      'https://www.google.com/s2/favicons?domain=sok.fi&sz=64',
    ],
    emoji: '🏪',
    color: 'bg-green-500',
    brandColors: {
      primary: '#009639', // S-Group green
      secondary: '#FFF'
    }
  },
  'Lidl': {
    name: 'Lidl',
    primaryLogo: 'https://logo.clearbit.com/lidl.fi',
    fallbackLogos: [
      'https://www.google.com/s2/favicons?domain=lidl.fi&sz=64',
      'https://logo.clearbit.com/lidl.com',
      'https://cdn.worldvectorlogo.com/logos/lidl-1.svg',
    ],
    emoji: '🏬',
    color: 'bg-blue-500',
    brandColors: {
      primary: '#0050AA', // Lidl blue
      secondary: '#FFED00' // Lidl yellow
    }
  },
  'Prisma': {
    name: 'Prisma',
    primaryLogo: 'https://logo.clearbit.com/prisma.fi',
    fallbackLogos: [
      'https://www.google.com/s2/favicons?domain=prisma.fi&sz=64',
      'https://www.google.com/s2/favicons?domain=sok.fi&sz=64',
    ],
    emoji: '🎪',
    color: 'bg-red-500',
    brandColors: {
      primary: '#E2001A', // Prisma red
      secondary: '#FFF'
    }
  },
  'Citymarket': {
    name: 'Citymarket',
    primaryLogo: '/logos/citymarket.svg', // Use local SVG first
    fallbackLogos: [
      'https://logo.clearbit.com/citymarket.fi',
      'https://www.google.com/s2/favicons?domain=citymarket.fi&sz=64',
      'https://logo.clearbit.com/kesko.fi',
    ],
    emoji: '🏢',
    color: 'bg-purple-500',
    brandColors: {
      primary: '#C00D1E', // Actual Citymarket red from SVG
      secondary: '#FF6900' // Orange from SVG
    }
  },
  'Alepa': {
    name: 'Alepa',
    primaryLogo: '/logos/alepa.svg', // Use local SVG first
    fallbackLogos: [
      'https://logo.clearbit.com/alepa.fi',
      'https://www.google.com/s2/favicons?domain=alepa.fi&sz=64',
      'https://www.google.com/s2/favicons?domain=sok.fi&sz=64',
    ],
    emoji: '🍎',
    color: 'bg-yellow-500',
    brandColors: {
      primary: '#FF0000', // Red from Alepa logo
      secondary: '#FFFFFF' // White from SVG
    }
  },
  'Sale': {
    name: 'Sale',
    primaryLogo: '/logos/sale.svg', // Use local SVG first
    fallbackLogos: [
      'https://logo.clearbit.com/sale.fi',
      'https://www.google.com/s2/favicons?domain=sale.fi&sz=64',
      'https://www.google.com/s2/favicons?domain=sok.fi&sz=64',
    ],
    emoji: '💰',
    color: 'bg-red-500',
    brandColors: {
      primary: '#E6393F', // Sale red from SVG
      secondary: '#FFF'
    }
  },
  'Muu': {
    name: 'Muu kauppa',
    primaryLogo: '',
    fallbackLogos: [],
    emoji: '🏪',
    color: 'bg-gray-500',
    brandColors: {
      primary: '#6B7280',
      secondary: '#FFF'
    }
  }
};

// Hook to get store logo with progressive fallback
export const useStoreLogo = (storeId: string) => {
  const storeData = STORE_LOGOS[storeId];
  
  if (!storeData) {
    return {
      logo: '🏪',
      isImage: false,
      color: 'bg-gray-500',
      brandColors: { primary: '#6B7280', secondary: '#FFF' }
    };
  }

  // Try primary logo first, then fallbacks
  const tryLogo = (logoUrl: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = logoUrl;
    });
  };

  return {
    logo: storeData.primaryLogo || storeData.emoji,
    fallbacks: storeData.fallbackLogos,
    emoji: storeData.emoji,
    isImage: !!storeData.primaryLogo,
    color: storeData.color,
    brandColors: storeData.brandColors,
    tryLogo
  };
};